import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Camera, ScanLine, ShieldAlert, SlidersHorizontal, Lightbulb, ChevronRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
interface Tip {
  ingredient: string;
  why: string;
  found: string;
}
const tips: Tip[] = [{
  ingredient: 'Red 40 & Yellow 5/6',
  why: 'Artificial food dyes linked to hyperactivity in children and possible carcinogenic effects. Banned or restricted in several countries.',
  found: 'Candy, soft drinks, cereals, snack chips'
}, {
  ingredient: 'High-Fructose Corn Syrup',
  why: 'A highly processed sweetener tied to obesity, insulin resistance, and fatty liver disease when consumed in excess.',
  found: 'Sodas, sauces, baked goods, flavored yogurt'
}, {
  ingredient: 'Monosodium Glutamate (MSG)',
  why: 'A flavor enhancer that some people are sensitive to, potentially causing headaches and flushing. Best consumed in moderation.',
  found: 'Instant noodles, chips, frozen meals, soups'
}, {
  ingredient: 'Sodium Benzoate',
  why: 'A preservative that can form benzene (a known carcinogen) when combined with vitamin C in acidic drinks.',
  found: 'Fruit juices, soft drinks, salad dressings'
}, {
  ingredient: 'Partially Hydrogenated Oils (Trans Fats)',
  why: 'Raise "bad" LDL cholesterol while lowering "good" HDL cholesterol, significantly increasing heart disease risk.',
  found: 'Margarine, fried foods, packaged pastries'
}, {
  ingredient: 'Mono & Diglycerides',
  why: 'Emulsifiers that can hide trans fats and may disrupt gut bacteria with frequent consumption.',
  found: 'Bread, ice cream, peanut butter, coffee creamer'
}];
const steps = [{
  icon: Camera,
  title: 'Step 1 — Open the Scanner',
  image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vxdue6obzwd6hjhd7fonaj/c2dc3655-bf08-4072-907a-0e44a0f2759c/IMG_4140_2.png",
  alt: 'IntelliEats home screen with Camera, Gallery, and Barcode scan options',
  body: 'From the home screen, choose how you want to scan. Tap Camera to photograph the ingredient label directly, Gallery to upload an existing photo, or Barcode to look up a product. Point your camera at the ingredient list — not the front of the package — and make sure the text is well-lit and in focus. Remember: the accuracy of detection depends on the quality of the image.'
}, {
  icon: ScanLine,
  title: 'Step 2 — Let IntelliEats Analyze',
  image: "/IMG_4142.png",
  alt: 'Scan Complete modal showing a quick summary of ingredients to avoid',
  body: 'Once you capture the label, IntelliEats reads the text and instantly analyzes every ingredient. You will see a Scan Complete summary that tells you at a glance whether any Ingredients to Avoid or Limited Intake ingredients were found. Tap View Results to dig into the full breakdown.'
}, {
  icon: ShieldAlert,
  title: 'Step 3 — Review Your Results',
  image: "/IMG_4145.png",
  alt: 'Results screen listing ingredients to avoid like Red 40 and Yellow 6',
  body: 'The results screen clearly separates Ingredients to Avoid (shown in red, such as Red 40, Yellow 6, Yellow 5, and Dextrin) from Limited Intake Ingredients (such as Monosodium glutamate). Tap any highlighted ingredient to learn why it was flagged and how it may affect your health. These two categories are always free.'
}, {
  icon: SlidersHorizontal,
  title: 'Step 4 — Personalize Detection Settings',
  image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vxdue6obzwd6hjhd7fonaj/c2dc3655-bf08-4072-907a-0e44a0f2759c/two.png",
  alt: 'Detection settings screen with dietary restriction toggles',
  body: 'Head to Settings to tailor the scanner to your own diet. Enable detection for dietary restrictions like Vegetarian, Vegan, Nuts, Gluten, Lactose, and Keto so IntelliEats flags ingredients that conflict with how you eat. Now every scan is personalized to you.'
}];
export function HowToGuide() {
  const [tipIndex, setTipIndex] = useState(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'How to Use IntelliEats to Scan Your Pantry | IntelliEats';
  }, []);
  const tip = tips[tipIndex];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-2676" data-mp="29gzjjxsxecdw">
      
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2677" data-mp="2ef9atxbj4su8">
        {/* Header */}
        <header className="mb-12" data-id="element-2678" data-mp="1gu4zv33xnh1h">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-2679" data-mp="1gqqz1zax3fw5">
            
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2680" data-mp="1qpykl9rt2guc" />
            Back to Home
          </Link>
          <div className="inline-block px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-bold tracking-wider uppercase mb-6" data-id="element-2681" data-mp="1vhzrsha49hdi">
            How-To Guide
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-2682" data-mp="1315nh9zg62y5">
            How to Use IntelliEats to Scan Your Pantry
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-2683" data-mp="vfjuz09g5nug">
            Scan a product label and instantly detect unhealthy ingredients and
            dietary-restriction conflicts in everything sitting in your pantry.
            This step-by-step guide walks you through the entire process.
          </p>
        </header>

        {/* Intro */}
        <div className="prose prose-lg prose-slate max-w-none mb-12" data-id="element-2684" data-mp="ck3hsxatosko">
          <p className="text-slate-700 leading-relaxed" data-id="element-2685" data-mp="7slfo5x2cly4">
            Your pantry is full of packaged foods, and every one of them carries
            an ingredient label that can be hard to decode. Long chemical names,
            hidden sugars, and artificial additives make it nearly impossible to
            tell at a glance whether something is good for you. IntelliEats was
            built to solve exactly this problem. Instead of squinting at tiny
            print and searching the internet for unfamiliar ingredient names,
            you simply point your phone at the label and let the app do the
            heavy lifting. In just a few seconds you will know which ingredients
            are safe, which ones you should limit, and which ones to avoid
            entirely.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-2686" data-mp="36bmy12xzvbp1">
            The process is designed to be effortless. Whether you are doing a
            full pantry clean-out, comparing two products at the grocery store,
            or just curious about that snack in your bag, the workflow is the
            same: scan, analyze, and decide. Below, we break down each step so
            you can get the most out of every scan.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-12 mb-16" data-id="element-2687" data-mp="3p8o6wyuo5vy9">
          {steps.map((step, index) => <motion.div key={step.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.05
        }} className="grid sm:grid-cols-2 gap-8 items-center" data-id="element-2688" data-mp="gtwa1cvf6faj" data-mpi={index}>
            
              <div className={index % 2 === 1 ? 'sm:order-2' : ''} data-id="element-2689" data-mp="14r2b48glsxae">
                <div className="flex items-center gap-3 mb-3" data-id="element-2690" data-mp="14sdfa9dyo721">
                  <span className="bg-brand-100 text-brand-600 p-2 rounded-xl" data-id="element-2691" data-mp="zrzixolq2lju">
                    <step.icon className="w-5 h-5" data-id="element-2692" data-mp="c2wp7idtd28z" />
                  </span>
                  <h2 className="text-xl font-bold text-slate-900 m-0" data-id="element-2693" data-mp="80ryosinwlqk">
                    {step.title}
                  </h2>
                </div>
                <p className="text-slate-700 leading-relaxed" data-id="element-2694" data-mp="m1ci9qweill4">{step.body}</p>
              </div>
              <div className={index % 2 === 1 ? 'sm:order-1' : ''} data-id="element-2695" data-mp="gzdhmos1q2rv">
                <img src={step.image} alt={step.alt} className="w-full max-w-[260px] mx-auto rounded-3xl shadow-xl border border-slate-100" data-id="element-2696" data-mp="1xeewwgexx12n" />
              
              </div>
            </motion.div>)}
        </div>

        {/* Why It Matters */}
        <div className="prose prose-lg prose-slate max-w-none mb-16" data-id="element-2697" data-mp="3mu8x1goiphzz">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2698" data-mp="3qyqf9bf8bn8n">
            Detecting Unhealthy & Dietary-Restriction Ingredients
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-2699" data-mp="4icerx38y9wi">
            IntelliEats does more than spot generally "unhealthy" additives. It
            also understands your personal dietary needs. If you are vegetarian,
            vegan, keto, or managing allergies to nuts, gluten, or lactose, the
            app will flag any ingredient that conflicts with your chosen
            restrictions. This means a single scan protects you from both
            long-term health risks — like artificial dyes and trans fats — and
            immediate concerns like an allergen hiding under an unfamiliar name.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-2700" data-mp="2p81sg6s7rpnm">
            By scanning everything in your pantry once, you build a clear
            picture of what you and your family are really eating. Over time,
            you will start to recognize the worst offenders on your own, shop
            smarter, and replace problematic products with cleaner alternatives.
            Healthier choices start with understanding what is really inside the
            package — and IntelliEats makes that understanding instant.
          </p>
        </div>

        {/* Tip of the Day */}
        <div className="bg-gradient-to-br from-accent-50 to-brand-50 rounded-3xl p-8 sm:p-10 border border-accent-100" data-id="element-2701" data-mp="2vots4ga4m4p4">
          <div className="flex items-center gap-3 mb-6" data-id="element-2702" data-mp="2qp7e44mh4kf9">
            <span className="bg-accent-400 text-white p-2 rounded-xl" data-id="element-2703" data-mp="2qrtmu7l3p5z2">
              <Lightbulb className="w-6 h-6" data-id="element-2704" data-mp="2mnlwk5yzvhyq" />
            </span>
            <h2 className="text-2xl font-bold text-slate-900 m-0" data-id="element-2705" data-mp="1fb3eoix4a42t">
              Tip of the Day
            </h2>
          </div>

          <AnimatePresence mode="wait" data-id="element-2706" data-mp="1b8733c5zv88d">
            <motion.div key={tipIndex} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-id="element-2707" data-mp="1bbcnie3ohlxr">
              
              <div className="flex items-center gap-2 mb-3" data-id="element-2708" data-mp="3oyvz1g8fv9od">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 text-xs font-bold rounded-full" data-id="element-2709" data-mp="7ixkhhut0c5t">
                  <AlertTriangle className="w-3 h-3" data-id="element-2710" data-mp="2kcj3f3tfmfs" />
                  AVOID
                </span>
                <h3 className="text-lg font-bold text-slate-900 m-0" data-id="element-2711" data-mp="gjlzaes0ugjr">
                  {tip.ingredient}
                </h3>
              </div>
              <p className="text-slate-700 mb-3" data-id="element-2712" data-mp="2ibid5v1nw2hp">{tip.why}</p>
              <p className="text-sm text-slate-500 m-0" data-id="element-2713" data-mp="2idupnsonhknd">
                <span className="font-semibold text-slate-700" data-id="element-2714" data-mp="2ddqjavhgi6av">
                  Commonly found in:
                </span>{' '}
                {tip.found}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-6" data-id="element-2715" data-mp="xt7o5lthji83">
            <span className="text-sm text-slate-500" data-id="element-2716" data-mp="k34a3i4reutq">
              Tip {tipIndex + 1} of {tips.length}
            </span>
            <button onClick={() => setTipIndex(prev => (prev + 1) % tips.length)} className="inline-flex items-center px-4 py-2 bg-white border border-brand-200 text-brand-700 text-sm font-semibold rounded-full hover:bg-brand-50 transition-colors" data-id="element-2717" data-mp="o2d3umzsuspa">
              
              Next Tip
              <ChevronRight className="w-4 h-4 ml-1" data-id="element-2718" data-mp="14s0xwdulky5" />
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12" data-id="element-2719" data-mp="55bw4uev2ehn">
          <Link to="/#download" className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-full hover:bg-brand-700 transition-colors shadow-lg shadow-brand-200/50" data-id="element-2720" data-mp="1vgyciwanm3qz">
            
            Download IntelliEats & Scan Your Pantry
          </Link>
        </div>
      </article>
    </motion.div>;
}